import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, ArrowRight, ShieldCheck, Clock3 } from "lucide-react";

const fmt = (n) => (n != null ? n.toLocaleString("tr-TR") : "-");

export default function TransferApprovalPublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/transfer/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Bağlantı geçersiz"));
  }, [token]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/public/transfer/${token}/respond`, { action, note });
      setDone(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  if (err && !data)
    return <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6"><p className="text-slate-500" data-testid="transfer-error">{err}</p></div>;
  if (!data)
    return <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6"><p className="text-slate-400">Yükleniyor...</p></div>;

  const overall = done?.status || data.status;
  const myStatus = done?.my_status || data.my_status;
  const finalStatus = overall !== "pending_approval" ? overall : (myStatus && myStatus !== "pending" ? "waiting_others" : null);
  const summary = data.approvers_summary || [];

  return (
    <div className="min-h-screen bg-[#F5F7FA] py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">İhale Devir Onayı</div>
        </div>

        <div className="border border-slate-200 bg-white p-6 space-y-1">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">İhale</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter" data-testid="transfer-request-title">{data.request_title}</h1>
          {data.approver_name && <p className="text-sm text-slate-500">Sayın {data.approver_name}, aşağıdaki devir işlemi onayınıza sunulmuştur.</p>}
          {summary.length > 1 && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 mt-2" data-testid="transfer-multi-note">
              Bu devir için {summary.length} onaylayıcı belirlenmiştir. Devir ancak tüm onaylayıcılar onayladığında uygulanır.
            </p>
          )}
        </div>

        {summary.length > 1 && (
          <div className="border border-slate-200 bg-white p-4 flex flex-wrap gap-2" data-testid="transfer-approvers-progress">
            {summary.map((a) => (
              <span key={a.name} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border ${a.status === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : a.status === "declined" ? "bg-red-50 border-red-200 text-red-600" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                {a.status === "approved" ? <CheckCircle2 size={12} /> : a.status === "declined" ? <XCircle size={12} /> : null}
                <strong>{a.name}</strong> — {a.status === "approved" ? "Onayladı" : a.status === "declined" ? "Reddetti" : "Bekleniyor"}
              </span>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-center" data-testid="transfer-summary">
          <div className="border border-red-200 bg-red-50/40 p-5">
            <div className="text-[10px] uppercase tracking-wider text-red-500">Mevcut Kazanan</div>
            <div className="font-heading text-lg font-extrabold tracking-tight mt-1">{data.from_company}</div>
            <div className="text-sm text-slate-600">{fmt(data.from_price)} {data.from_currency}</div>
          </div>
          <ArrowRight size={22} className="text-slate-400 mx-auto rotate-90 sm:rotate-0" />
          <div className="border border-emerald-300 bg-emerald-50/50 p-5">
            <div className="text-[10px] uppercase tracking-wider text-emerald-600">Devredilecek Firma</div>
            <div className="font-heading text-lg font-extrabold tracking-tight mt-1">{data.to_company}</div>
            <div className="text-sm text-slate-600">{fmt(data.to_price)} {data.to_currency}</div>
          </div>
        </div>

        {data.note && (
          <div className="border border-slate-200 bg-white p-4 text-sm text-slate-600" data-testid="transfer-note">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-1">Devir Notu</span>
            {data.note}
          </div>
        )}

        {finalStatus ? (
          <div className={`border p-6 text-center space-y-2 ${finalStatus === "approved" ? "border-emerald-300 bg-emerald-50" : finalStatus === "waiting_others" ? "border-amber-300 bg-amber-50" : "border-red-200 bg-red-50"}`} data-testid="transfer-result">
            {finalStatus === "approved"
              ? <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
              : finalStatus === "waiting_others"
              ? <Clock3 size={32} className="mx-auto text-amber-500" />
              : <XCircle size={32} className="mx-auto text-red-500" />}
            <p className="font-semibold">
              {finalStatus === "approved"
                ? "Devir onaylandı ve uygulandı."
                : finalStatus === "waiting_others"
                ? (myStatus === "approved" ? "Onayınız kaydedildi. Devir, diğer onaylayıcıların kararı sonrasında uygulanacaktır." : "Kararınız kaydedildi.")
                : "Devir talebi reddedildi."}
            </p>
            <p className="text-xs text-slate-500 flex items-center justify-center gap-1"><ShieldCheck size={12} /> Kararınız dijital olarak kayıt altına alınmıştır.</p>
          </div>
        ) : (
          <div className="border border-slate-200 bg-white p-6 space-y-4" data-testid="transfer-actions">
            <div className="space-y-1.5">
              <Label>Karar Notu (opsiyonel)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="transfer-decision-note" className="rounded-none bg-white border-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => respond("approve")} disabled={busy} data-testid="transfer-approve-button" className="rounded-none h-12 bg-emerald-600 text-white hover:bg-emerald-500 font-semibold gap-2">
                <CheckCircle2 size={16} /> Devri Onayla
              </Button>
              <Button onClick={() => respond("decline")} disabled={busy} variant="outline" data-testid="transfer-decline-button" className="rounded-none h-12 border-red-300 text-red-600 hover:bg-red-50 font-semibold gap-2">
                <XCircle size={16} /> Reddet
              </Button>
            </div>
            <p className="text-xs text-slate-400 flex items-center gap-1"><ShieldCheck size={12} /> Kararınız adınız, e-postanız ve zaman damgasıyla dijital olarak kaydedilecektir.</p>
            {err && <p className="text-sm text-red-500">{err}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
